// Spec Driven App Types

export interface Skill {
  id: string;
  name: string;
  description: string;
  category: 'frontend' | 'backend' | 'data' | 'ai' | 'integration';
  enabled: boolean;
}

export interface Requirement {
  id: string;
  title: string;
  description: string;
  priority: 'must' | 'should' | 'could' | 'wont';
  status: 'draft' | 'approved' | 'implemented';
}

export interface AppSpec {
  id: string;
  name: string;
  subdomain: string;
  displayName: string;
  description: string;
  icon: string;
  skills: Skill[];
  requirements: Requirement[];
  steering: SteeringConfig;
  status: 'draft' | 'generating' | 'deployed' | 'failed';
  createdAt: string;
  updatedAt: string;
  createdBy: string;
}

export interface SteeringConfig {
  hasBackend: boolean;
  backendType: 'nodejs' | 'python' | 'none';
  authRequired: boolean;
  allowedGroups: string[];
  dataStorage: 's3' | 'dynamodb' | 'mysql' | 'none';
  apiPrefix: string;
  // MySQL specific config
  mysqlDbName?: string;
  // Chat panel configuration for deployed app
  chatEnabled?: 'all' | 'admin' | 'disabled';
  // Generation method
  generationMethod?: 'template' | 'llm';
  // Backend server selection
  backendServer?: 'azure' | 'ec2';
  // Claude Code SDK capabilities
  sdkEnabled?: boolean;
  sdkChat?: boolean;
  sdkCrosscheck?: boolean;
  sdkDocgen?: boolean;
  sdkLlmBridge?: boolean;
  // LLM model for chat features
  llmModel?: string;
  // Skip options for wizard
  skipCodeGen?: boolean;
  skipDocs?: boolean;
  skipVideos?: boolean;
  skipGithub?: boolean;
}

export interface DeploymentConfig {
  s3Bucket: string;
  cloudFrontId: string;
  domain: string;
  apiEndpoint?: string;
  status: 'pending' | 'creating' | 'active' | 'failed';
}

export interface GeneratedApp {
  spec: AppSpec;
  deployment: DeploymentConfig;
  files: GeneratedFile[];
  portalCardId: string;
}

export interface GeneratedFile {
  path: string;
  content: string;
  type: 'component' | 'config' | 'style' | 'util' | 'api' | 'test';
}

export interface User {
  username: string;
  email: string;
  groups: string[];
  isAdmin: boolean;
  // true when landed via guest-admin mode (Cognito login disabled) rather than
  // a real Cognito session.
  guest?: boolean;
}

export interface SkillTemplate {
  id: string;
  name: string;
  description: string;
  category: Skill['category'];
  codeTemplate: string;
  dependencies: string[];
  source?: 'internal' | 'external';
  installs?: number;
  topSource?: string;
}

export interface ExternalSkill {
  id: string;
  name: string;
  installs: number;
  topSource: string;
}

export interface SkillsApiResponse {
  skills: ExternalSkill[];
  hasMore: boolean;
}

export const AVAILABLE_SKILLS: SkillTemplate[] = [
  {
    id: 'crud-api',
    name: 'CRUD API',
    description: 'Create, Read, Update, Delete operations for data management',
    category: 'backend',
    codeTemplate: 'crud',
    dependencies: ['express', 'aws-sdk']
  },
  {
    id: 'file-upload',
    name: 'File Upload',
    description: 'Upload and manage files with S3 storage',
    category: 'backend',
    codeTemplate: 'file-upload',
    dependencies: ['multer', '@aws-sdk/client-s3']
  },
  {
    id: 'data-table',
    name: 'Data Table',
    description: 'Interactive data table with sorting, filtering, pagination',
    category: 'frontend',
    codeTemplate: 'data-table',
    dependencies: []
  },
  {
    id: 'form-builder',
    name: 'Form Builder',
    description: 'Dynamic form generation with validation',
    category: 'frontend',
    codeTemplate: 'form-builder',
    dependencies: []
  },
  {
    id: 'chart-visualization',
    name: 'Charts & Visualization',
    description: 'Data visualization with charts and graphs',
    category: 'data',
    codeTemplate: 'charts',
    dependencies: ['recharts']
  },
  {
    id: 'ai-chat',
    name: 'AI Chat Interface',
    description: 'Chat interface with AI assistant capabilities',
    category: 'ai',
    codeTemplate: 'ai-chat',
    dependencies: ['openai']
  },
  {
    id: 'cognito-auth',
    name: 'Cognito Authentication',
    description: 'User authentication with AWS Cognito',
    category: 'integration',
    codeTemplate: 'cognito-auth',
    dependencies: ['aws-amplify', '@aws-amplify/ui-react']
  },
  {
    id: 'export-csv',
    name: 'CSV Export',
    description: 'Export data to CSV format',
    category: 'data',
    codeTemplate: 'export-csv',
    dependencies: []
  },
  {
    id: 'markdown-viewer',
    name: 'Markdown Viewer',
    description: 'View and render Markdown content',
    category: 'frontend',
    codeTemplate: 'markdown-viewer',
    dependencies: ['react-markdown', 'remark-gfm']
  },
  {
    id: 'kanban-board',
    name: 'Kanban Board',
    description: 'Drag-and-drop kanban board for task management',
    category: 'frontend',
    codeTemplate: 'kanban',
    dependencies: ['@dnd-kit/core', '@dnd-kit/sortable']
  }
];

export const ICON_OPTIONS = [
  'book', 'chart', 'clipboard', 'roadmap', 'settings',
  'activity', 'notebook-ai', 'code', 'database', 'gear',
  'users', 'folder', 'file', 'star', 'rocket'
];
